import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import AppIcon from './AppIcon';
import { Users, HelpCircle, LogOut, Menu, X, ChevronLeft } from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Dashboard', href: '/dashboard', icon: 'home' },
  { label: 'Family Tree', href: '/family-tree', icon: 'tree' },
  { label: 'Members', href: '/members', icon: 'users' },
  { label: 'Memories', href: '/memories', icon: 'image' },
  { label: 'Cultural', href: '/cultural-terms', icon: 'book' },
  { label: 'Invitations', href: '/invitations', icon: 'mail' },
  { label: 'Settings', href: '/settings', icon: 'settings' },
];

const MOBILE_NAV = [
  { label: 'Home', href: '/dashboard', icon: 'home' },
  { label: 'Tree', href: '/family-tree', icon: 'tree' },
  { label: 'Members', href: '/members', icon: 'users' },
  { label: 'Memories', href: '/memories', icon: 'image' },
  { label: 'More', href: '/more', icon: 'more' },
];

/**
 * AppNav provides a sidebar (desktop) and bottom nav (mobile).
 */
function AppNav({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const isActive = (href) => {
    return location.pathname === href ||
      (href !== '/dashboard' && location.pathname.startsWith(href));
  };

  // Don't show nav on login page
  const hideNav = location.pathname === '/login';

  if (hideNav) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header Bar */}
      <header className="md:hidden bg-white shadow-sm sticky top-0 z-30 flex items-center justify-between h-14 px-4">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
        >
          <Menu size={20} />
        </button>
        <a href="/dashboard" className="flex items-center gap-2 text-gray-900">
          <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Users className="w-4 h-4 text-white" />
          </div>
          <span className="text-base font-bold">Mien Family</span>
        </a>
        <div className="w-10" /> {/* spacer */}
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Slide-in Sidebar */}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 flex flex-col transition-transform duration-300 w-64 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Mobile Sidebar Header */}
        <div className="flex items-center justify-between h-14 px-4 border-b border-gray-200">
          <a href="/dashboard" className="flex items-center gap-2 text-gray-900">
            <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
            <span className="text-base font-bold">Mien Family</span>
          </a>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Mobile Nav Items */}
        <nav className="flex-1 py-3 px-3 overflow-y-auto">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-0.5 ${
                isActive(item.href)
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <AppIcon name={item.icon} size={20} />
              <span>{item.label}</span>
            </a>
          ))}
        </nav>

        {/* Mobile Sidebar Footer */}
        <div className="border-t border-gray-200 p-3">
          <a
            href="/help"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors mb-1"
          >
            <HelpCircle size={20} />
            <span>Help</span>
          </a>
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700">
              {user?.email ? user.email[0].toUpperCase() : '?'}
            </div>
            <span className="text-sm text-gray-700 truncate flex-1">
              {user?.email ? user.email.split('@')[0] : 'User'}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors w-full"
          >
            <LogOut size={20} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col fixed inset-y-0 left-0 z-30 bg-white border-r border-gray-200 transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-56'
        }`}
      >
        {/* Desktop Sidebar Header */}
        <div className="flex items-center justify-between h-14 px-3 border-b border-gray-200">
          {!collapsed && (
            <a href="/dashboard" className="flex items-center gap-2 text-gray-900 overflow-hidden">
              <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-bold whitespace-nowrap">Mien Family</span>
            </a>
          )}
          {collapsed && (
            <a href="/dashboard" className="flex items-center justify-center w-full">
              <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-white" />
              </div>
            </a>
          )}
        </div>

        {/* Desktop Nav Items */}
        <nav className="flex-1 py-3 px-2 overflow-y-auto">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-0.5 ${
                collapsed ? 'justify-center' : ''
              } ${
                isActive(item.href)
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <AppIcon name={item.icon} size={20} />
              {!collapsed && <span>{item.label}</span>}
            </a>
          ))}
        </nav>

        {/* Desktop Sidebar Footer */}
        <div className="border-t border-gray-200 p-2">
          <a
            href="/help"
            title={collapsed ? 'Help' : undefined}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors mb-1 ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <HelpCircle size={20} />
            {!collapsed && <span>Help</span>}
          </a>

          {!collapsed && (
            <div className="flex items-center gap-2 px-3 py-2 mb-1">
              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                {user?.email ? user.email[0].toUpperCase() : '?'}
              </div>
              <span className="text-sm text-gray-700 truncate">
                {user?.email ? user.email.split('@')[0] : 'User'}
              </span>
            </div>
          )}

          <button
            onClick={handleLogout}
            title={collapsed ? 'Sign out' : undefined}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors w-full ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <LogOut size={20} />
            {!collapsed && <span>Sign out</span>}
          </button>

          {/* Collapse Toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors w-full mt-1 ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <ChevronLeft size={20} className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`pb-16 md:pb-0 transition-all duration-300 md:h-screen md:overflow-hidden ${
          collapsed ? 'md:ml-16' : 'md:ml-56'
        }`}
      >
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 z-10">
        <div className="grid grid-cols-5 gap-1 py-1.5 px-2">
          {MOBILE_NAV.map((item) => {
            const active = location.pathname === item.href;
            return (
              <a
                key={item.label}
                href={item.href}
                className={`flex flex-col items-center py-1 transition-colors ${
                  active ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <AppIcon name={item.icon} size={20} />
                <span className="text-[10px] mt-0.5">{item.label}</span>
              </a>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default AppNav;